import { useId, useLayoutEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Familia } from '../core/produtos';
import { Icone } from './icones';
import './menu-produto.css';

type Props = HTMLAttributes<HTMLElement> & {
  familia?: Familia;
  cor?: string;
  /** No catálogo de peças, o padrão é embutido. A prévia completa pode fixar seu menu único. */
  fixo?: boolean;
  /**
   * Desenha a pílula no lugar, no fluxo da página, em qualquer tela (F1,
   * rodada 2, a pedido da F2): o site e as vitrines usam a peça oficial em
   * vez de copiar o desenho. Fora do `/_ds` o padrão é virar portal da
   * `BarraDoSuite`; com `embutido` ela nem procura a barra.
   */
  embutido?: boolean;
};

/** Um portal único mantém as duas pills fora das animações do conteúdo. */
export function BarraDoSuite({ children }: { children: ReactNode }) {
  const barra = useRef<HTMLDivElement>(null);
  const origem = useRef<HTMLSpanElement>(null);
  const [familia, setFamilia] = useState('plataforma');
  useLayoutEffect(() => {
    const casco = origem.current?.closest('[data-familia]');
    if (!casco) return;
    const atualizar = () => setFamilia(casco.getAttribute('data-familia') || 'plataforma');
    atualizar();
    const observador = new MutationObserver(atualizar);
    observador.observe(casco, { attributes: true, attributeFilter: ['data-familia'] });
    return () => observador.disconnect();
  }, []);
  useLayoutEffect(() => {
    const no = barra.current;
    const principal = no?.querySelector<HTMLElement>('.navegacao-suite__principal');
    if (!no || !principal) return;
    const preferencia = window.matchMedia('(prefers-reduced-motion: reduce)');
    let esquerda = no.getBoundingClientRect().left + principal.offsetLeft;
    let movimento: Animation | undefined;
    let quadro = 0;
    const ajustarMais = () => {
      const atalhos = Array.from(no.querySelectorAll<HTMLAnchorElement>('.suite-dock__atalhos>a,.ds-dock>a'));
      const visiveis = new Set(atalhos.filter(a => getComputedStyle(a).display !== 'none').map(a => a.getAttribute('href')));
      no.querySelectorAll<HTMLAnchorElement>('.suite-dock__menu a,.ds-dock__menu a').forEach(a => { a.hidden = visiveis.has(a.getAttribute('href')); });
      no.querySelectorAll<HTMLElement>('.suite-dock__grupo').forEach(titulo => {
        let proximo = titulo.nextElementSibling, temItem = false;
        while (proximo && !proximo.matches('.suite-dock__grupo')) { if (proximo.matches('a') && !(proximo as HTMLElement).hidden) temItem = true; proximo = proximo.nextElementSibling; }
        titulo.hidden = !temItem;
      });
      const ativo = atalhos.find(a => a.matches('.active,.ativa'))?.getAttribute('href') ?? '';
      if (/\/home$|#inicio-painel-de-abertura$|#telas$/.test(ativo)) {
        const slot = no.querySelector<HTMLElement>('.navegacao-suite__produto');
        if (slot) { delete slot.dataset.produtoAberto; delete slot.dataset.rolagem; }
      }
    };
    const medir = () => {
      ajustarMais();
      const proxima = no.getBoundingClientRect().left + principal.offsetLeft;
      const deslocamento = esquerda - proxima;
      esquerda = proxima;
      if (preferencia.matches || Math.abs(deslocamento) < 1) return;
      const atual = new DOMMatrixReadOnly(getComputedStyle(principal).transform).m41;
      movimento?.cancel();
      movimento = principal.animate([
        { transform: `translateX(${deslocamento + atual}px)` }, { transform: 'translateX(0)' },
      ], { duration: 460, easing: 'cubic-bezier(.16,1,.3,1)' });
    };
    const agendar = () => { cancelAnimationFrame(quadro); quadro = requestAnimationFrame(medir); };
    const tamanho = new ResizeObserver(agendar);
    tamanho.observe(no);
    const conteudo = new MutationObserver(agendar);
    conteudo.observe(no, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    ajustarMais();
    window.addEventListener('resize', agendar);
    const reduzir = () => { if (preferencia.matches) movimento?.cancel(); };
    preferencia.addEventListener('change', reduzir);
    return () => { window.removeEventListener('resize', agendar); cancelAnimationFrame(quadro); tamanho.disconnect(); conteudo.disconnect(); movimento?.cancel(); preferencia.removeEventListener('change', reduzir); };
  }, []);
  return <><span ref={origem} className="menu-produto-origem" aria-hidden="true" />{typeof document !== 'undefined' && createPortal(
    <div ref={barra} className="navegacao-suite suite-modern" data-familia={familia} data-theme="light">
      <div className="navegacao-suite__principal">{children}</div>
      <div className="navegacao-suite__produto" />
    </div>, document.body,
  )}</>;
}

/** Navegação do produto preserva os links e permissões de quem a compõe. */
export function MenuDoProduto({ children, familia = 'plataforma', cor, fixo: fixoPedido, embutido = false, className = '', style, ...props }: Props) {
  const fixo = embutido ? false : fixoPedido;
  const origem = useRef<HTMLSpanElement>(null);
  const trilho = useRef<HTMLDivElement>(null);
  const pilula = useRef<HTMLElement>(null);
  const [flutuante, setFlutuante] = useState(fixo ?? false);
  const [destino, setDestino] = useState<HTMLElement | null>(null);
  const [limites, setLimites] = useState({ inicio: true, fim: true });
  const id = useId();
  const [dica, setDica] = useState<{ texto: string; x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    const noCatalogo = Boolean(origem.current?.closest('.ds'));
    setFlutuante(fixo ?? !noCatalogo);
    setDestino(embutido ? null : document.querySelector<HTMLElement>('.navegacao-suite__produto'));
  }, [fixo, embutido]);

  useLayoutEffect(() => {
    const lista = trilho.current;
    if (!lista) return;
    const identidade = `${familia}:${props['aria-label'] ?? ''}`;
    if (destino?.dataset.produtoAberto === identidade) lista.scrollLeft = Number(destino.dataset.rolagem || 0);
    const guardar = () => { if (destino?.dataset.produtoAberto === identidade) destino.dataset.rolagem = String(lista.scrollLeft); };
    const reduzir = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const medir = () => setLimites({ inicio: lista.scrollLeft <= 2, fim: lista.scrollWidth - lista.clientWidth - lista.scrollLeft <= 2 });
    const revelar = (item: HTMLElement | null) => {
      if (!item) return;
      const margem = 8;
      const r = item.getBoundingClientRect();
      const l = lista.getBoundingClientRect();
      const delta = r.left < l.left + margem ? r.left - l.left - margem : r.right > l.right - margem ? r.right - l.right + margem : 0;
      if (delta) lista.scrollBy({ left: delta, behavior: reduzir() ? 'instant' : 'smooth' });
    };
    const ativo = () => revelar(lista.contains(document.activeElement) ? (document.activeElement as HTMLElement).closest<HTMLElement>('a,button') : lista.querySelector<HTMLElement>('[aria-current="page"], [aria-current="true"], [aria-selected="true"], .active, .ativo')); 
    const foco = (e: FocusEvent) => revelar((e.target as HTMLElement).closest<HTMLElement>('a,button'));
    const observador = new ResizeObserver(() => { medir(); ativo(); });
    observador.observe(lista);
    for (const item of lista.children) observador.observe(item);
    const mutacoes = new MutationObserver(() => { medir(); ativo(); });
    mutacoes.observe(lista, { subtree: true, attributes: true, attributeFilter: ['aria-current', 'aria-selected', 'class'], childList: true });
    lista.addEventListener('scroll', medir, { passive: true });
    lista.addEventListener('scroll', guardar, { passive: true });
    lista.addEventListener('focusin', foco);
    medir(); ativo();
    return () => { guardar(); lista.removeEventListener('scroll', guardar); observador.disconnect(); mutacoes.disconnect(); lista.removeEventListener('scroll', medir); lista.removeEventListener('focusin', foco); };
  }, [children, flutuante, destino]);

  /* A pill se revela a partir do dock, sem comprimir texto ou mover os alvos. */
  useLayoutEffect(() => {
    const no = pilula.current;
    const lista = trilho.current;
    if (!flutuante || !destino || !no || !lista) return;
    const identidade = `${familia}:${props['aria-label'] ?? ''}`;
    const repetido = destino.dataset.produtoAberto === identidade;
    if (!repetido) delete destino.dataset.rolagem;
    destino.dataset.produtoAberto = identidade;
    const preferencia = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (repetido || preferencia.matches) return;
    const animacoes: Animation[] = [];
    animacoes.push(no.animate([
      { clipPath: 'inset(0 calc(100% - 50px) 0 0 round 999px)', opacity: 0.45, translate: '-8px 0' },
      { clipPath: 'inset(0 0 0 0 round 999px)', opacity: 1, translate: '0 0' },
    ], { duration: 460, easing: 'cubic-bezier(.16,1,.3,1)' }));
    const visiveis = Array.from(lista.children).filter((item) => {
      const r = item.getBoundingClientRect(), l = lista.getBoundingClientRect();
      return r.right > l.left && r.left < l.right;
    });
    visiveis.forEach((item, indice) => animacoes.push(item.animate([
      { opacity: 0, translate: '-9px 0' }, { opacity: 1, translate: '0 0' },
    ], { duration: 280, delay: 55 + Math.min(indice, 8) * 22, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'backwards' })));
    const concluir = () => animacoes.forEach(a => a.cancel());
    const preferenciaMudou = () => { if (preferencia.matches) concluir(); };
    preferencia.addEventListener('change', preferenciaMudou);
    // Quem chega pelo teclado não precisa esperar uma animação para ver o foco.
    no.addEventListener('focusin', concluir);
    return () => { concluir(); preferencia.removeEventListener('change', preferenciaMudou); no.removeEventListener('focusin', concluir); };
  }, [flutuante, destino, familia, props['aria-label']]);

  useLayoutEffect(() => {
    const lista = trilho.current;
    if (!lista) return;
    const rotular = () => Array.from(lista.children).forEach(item => {
      const no = item as HTMLElement;
      const nome = no.getAttribute('aria-label') || no.textContent?.trim() || no.getAttribute('title') || '';
      if (nome) no.setAttribute('aria-label', nome);
      no.removeAttribute('title');
    });
    rotular();
    const observar = new MutationObserver(rotular);
    observar.observe(lista, { childList: true, subtree: true });
    const mostrar = (evento: Event) => {
      const item = (evento.target as HTMLElement).closest<HTMLElement>('a,button');
      if (!item || !lista.contains(item)) return;
      const r = item.getBoundingClientRect(), texto = item.getAttribute('aria-label') || '';
      setDica({ texto, x: Math.max(90, Math.min(innerWidth - 90, r.left + r.width / 2)), y: r.top - 14 });
    };
    const fechar = () => setDica(null);
    lista.addEventListener('pointerover', mostrar); lista.addEventListener('focusin', mostrar);
    lista.addEventListener('click', fechar); lista.addEventListener('pointerleave', fechar); lista.addEventListener('focusout', fechar); lista.addEventListener('scroll', fechar);
    return () => { observar.disconnect(); lista.removeEventListener('pointerover', mostrar); lista.removeEventListener('focusin', mostrar); lista.removeEventListener('click', fechar); lista.removeEventListener('pointerleave', fechar); lista.removeEventListener('focusout', fechar); lista.removeEventListener('scroll', fechar); };
  }, [flutuante, destino]);

  const rolar = (sentido: number) => {
    const lista = trilho.current;
    if (lista) lista.scrollBy({ left: sentido * Math.max(160, lista.clientWidth * .72), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  };
  const nav = (
    <nav ref={pilula} {...props} className={`menu-produto ${flutuante ? 'menu-produto--fixo' : 'menu-produto--embutido'} ${className}`} data-familia={familia} data-theme="light" style={{ ...(cor ? { '--menu-cor': cor } : {}), ...style } as CSSProperties}>
      <button className="menu-produto__seta" type="button" onClick={() => rolar(-1)} aria-label="Ver seções anteriores" aria-controls={id} disabled={limites.inicio} hidden={limites.inicio && limites.fim}>
        <Icone.SetaEsquerda tamanho={17} aria-hidden="true" />
      </button>
      <div id={id} ref={trilho} className="menu-produto__trilho">{children}</div>
      <button className="menu-produto__seta" type="button" onClick={() => rolar(1)} aria-label="Ver próximas seções" aria-controls={id} disabled={limites.fim} hidden={limites.inicio && limites.fim}>
        <Icone.SetaDireita tamanho={17} aria-hidden="true" />
      </button>
    </nav>
  );
  return <><span ref={origem} className="menu-produto-origem" data-fixo={flutuante} aria-hidden="true" />{flutuante && typeof document !== 'undefined' ? createPortal(nav, destino ?? document.body) : nav}{dica && createPortal(<div className="menu-produto__dica" role="tooltip" style={{ left: dica.x, top: dica.y }}>{dica.texto}</div>, document.body)}</>;
}
